"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import type { ComparisonRowDef } from "@highereducation/hen-ui";
import type { DataPoints } from "@/monarch/types/ProductCompare";

export interface ProductCompareMessages {
  unavailable: string;
  defaultRowDefs: ComparisonRowDef[];
  defaultHeader: string;
  defaultSubheader: string;
  tableNavAriaLabel: string;
  previousColumnAriaLabel: string;
  selectPageAriaLabel: string;
  nextColumnAriaLabel: string;
  currentProgram: string;
  currentCourse: string;
  earnCertificate: string;
  learnMore: string;
  partnerLogoAlt: string;
  certificates: (count: number) => string;
  newBadge: string;
  discountOff: (percent: number) => string;
  formatDuration: (weeks: number) => string;
}

const DATA_POINT_KEYS: DataPoints[] = [
  "description",
  "skills",
  "learningOutcome",
  "price",
  "duration",
  "skillLevel",
  "flexibility",
  "subject",
  "courseLanguage",
];

const ROW_TYPES: Record<DataPoints, ComparisonRowDef["type"]> = {
  description: "text",
  skills: "badgeList",
  learningOutcome: "dualText",
  price: "dualText",
  duration: "dualText",
  skillLevel: "dualText",
  flexibility: "dualText",
  subject: "dualText",
  courseLanguage: "dualText",
};

export function useProductCompareMessages(): ProductCompareMessages {
  const t = useTranslations("ProductCompare");

  return useMemo(() => {
    const defaultRowDefs: ComparisonRowDef[] = DATA_POINT_KEYS.map((key) => ({
      key,
      label: t(`labels.${key}` as Parameters<typeof t>[0]),
      type: ROW_TYPES[key],
    }));

    return {
      unavailable: t("unavailable"),
      defaultRowDefs,
      defaultHeader: t("defaultHeader"),
      defaultSubheader: t("defaultSubheader"),
      tableNavAriaLabel: t("tableNavAriaLabel"),
      previousColumnAriaLabel: t("previousColumnAriaLabel"),
      selectPageAriaLabel: t("selectPageAriaLabel"),
      nextColumnAriaLabel: t("nextColumnAriaLabel"),
      currentProgram: t("currentProgram"),
      currentCourse: t("currentCourse"),
      earnCertificate: t("earnCertificate"),
      learnMore: t("learnMore"),
      partnerLogoAlt: t("partnerLogoAlt"),
      certificates: (count: number) => t("certificates", { count }),
      newBadge: t("newBadge"),
      discountOff: (percent: number) => t("discountOff", { percent }),
      formatDuration: (weeks: number) => {
        if (weeks <= 6) {
          const unit = weeks === 1 ? t("week") : t("weeks");
          return `${weeks} ${unit}`;
        }
        const months = Math.ceil(weeks / 4);
        const unit = months === 1 ? t("month") : t("months");
        return `${months} ${unit}`;
      },
    };
  }, [t]);
}
